import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { DayStrip as DayStripData } from './resultsView';
import { formatMoney } from './quoteView';
import styles from './styles.module.css';

const VISIBLE_DAYS = 8;

interface DayStripProps {
  strip: DayStripData;
  selectedDay: string | null;
  onSelect: (day: string | null) => void;
}

/** Lowest price per departure day, eight at a time; click a day to show only that day's departures. */
const DayStrip: React.FC<DayStripProps> = ({
  strip,
  selectedDay,
  onSelect,
}) => {
  const [start, setStart] = useState(0);
  const total = strip.days.length;

  // Bring a newly selected day into view.
  useEffect(() => {
    const index = strip.days.findIndex((d) => d.day === selectedDay);
    if (index < 0) return;
    setStart((s) =>
      index < s
        ? index
        : index >= s + VISIBLE_DAYS
          ? index - VISIBLE_DAYS + 1
          : s
    );
  }, [selectedDay, strip.days]);

  if (total === 0) return null;
  const first = Math.min(start, Math.max(total - VISIBLE_DAYS, 0));
  const visible = strip.days.slice(first, first + VISIBLE_DAYS);

  return (
    <div className={styles.dayStrip} role='group' aria-label='Price by day'>
      <button
        type='button'
        className={styles.dayArrow}
        aria-label='Earlier days'
        disabled={first === 0}
        onClick={() => setStart(Math.max(first - VISIBLE_DAYS + 1, 0))}
      >
        ‹
      </button>
      {visible.map((cell) => {
        const selected = cell.day === selectedDay;
        return (
          <button
            type='button'
            key={cell.day}
            className={`${styles.dayCell} ${selected ? styles.dayCellSelected : ''}`}
            aria-pressed={selected}
            disabled={cell.price == null && !selected}
            onClick={() => onSelect(selected ? null : cell.day)}
          >
            <span className={styles.dayPrice}>
              {cell.price == null
                ? '—'
                : formatMoney(cell.price, strip.currency).replace(/\.00$/, '')}
            </span>
            <span className={styles.dayLabel}>
              {dayjs(cell.day).format('ddd D')}
            </span>
          </button>
        );
      })}
      <button
        type='button'
        className={styles.dayArrow}
        aria-label='Later days'
        disabled={first + VISIBLE_DAYS >= total}
        onClick={() =>
          setStart(Math.min(first + VISIBLE_DAYS - 1, total - VISIBLE_DAYS))
        }
      >
        ›
      </button>
    </div>
  );
};

export default DayStrip;
